// React
import { useCallback, useEffect, useMemo, useState } from 'react';

// Hooks
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

// API
import {
  UpdateChargerInformation,
  useFetchChargerDetailQuery,
  useFetchWifiPasswordQuery,
  useIsValidChargerMutation,
  useUpdateChargerMutation,
} from '../../services/charger.api';

// Selectors

// Components and Assets
import {
  Button,
  ButtonSize,
  ButtonType,
  Card,
  ColorType,
  getHexColorByType,
  Icon,
  Label,
  LabelType,
  Snackbar,
} from '../_ui';

// Utils
import { validateEmail } from '../_ui/modal/utils';
import { handleValidateId } from './utils';

// CSS

// Constants
import { Edit2, Info } from '../../assets/icons';
import { AnalyticsEvent, captureAnalyticsEvent } from '../../constant/Events';
import { TEXT } from '../../constant/Text.constant';
import { useAuth, USER_ROLE } from '../../hooks';
import { useCompany } from '../../hooks/useCompany';
import { useCompanyPermissions } from '../../hooks/useCompanyPermissions';
import { useAllowedFeatures } from '../../hooks/useFeaturePersonalization';
import { useNavigateWithSearchParam } from '../../hooks/useNavigateWithSearchParam';
import { RoutePath } from '../../routes';
import { useFetchLicenseByLicenseKeyQuery } from '../../services/license.api';
import { useLazyGetCircuitsQuery } from '../../services/pm.api';
import { useFetchModelsQuery } from '../../services/provision';
import { SCOPE_TYPE } from '../../services/utils';
import { Charger } from '../../stores/types';
import { Connectivity } from '../../stores/types/chargers.interface';
import { OWNER_TYPE } from '../../stores/types/sessions.interface';
import { Form } from '../_ui/Form.component';
import { AlertPosition, AlertType } from '../_ui/snack-bar/Snack-Bar.component';
import { AdditionalInfo } from './charger-detail/AdditionalInfo.component';
import { ChargerAutoStart } from './charger-detail/ChargerAutoStart.component';
import { ChargerDeployment } from './charger-detail/ChargerDeployment.component';
import { ChargerInfo } from './charger-detail/ChargerInfo.component';
import { ChargerSetting } from './charger-detail/ChargerSetting.component';
import { ChargerSummary } from './charger-detail/ChargerSummary.component';
import ChargerHistory from './charger-history/ChargerHistory.component';
import ChargerTerminal from './charger-terminal/ChargerTerminal.component';

export const ChargerDetail = () => {
  const { t } = useTranslation();
  const { chargerId: id } = useParams();
  const { navigate } = useNavigateWithSearchParam();
  const { switchCompany, userScope, companyId } = useCompany();
  const { canEditTerminals } = useCompanyPermissions(companyId);

  const { SIMULATED } = TEXT;

  const [updateCharger, updateChargerQueryResult] = useUpdateChargerMutation();
  const [isValidDisplayIdOCPP, setisValidDisplayIdOCPP] = useState({
    ocppId: true,
    displayId: true,
  });

  const [loadSkeletonAfterEdit, setLoadSkeletonAfterEdit] = useState(false);

  const { allowPayterView } = useAllowedFeatures();

  const [manufacturer, setManufacturer] = useState('');
  const [editEnabled, setEditEnabled] = useState(false);

  const {
    data: chargerData,
    isFetching: isChargerLoading,
    isLoading,
    error,
  } = useFetchChargerDetailQuery(
    {
      id: id!,
      scope: userScope!,
    },
    { pollingInterval: editEnabled ? 0 : 15000 },
  );

  const chargerCompanyId = useMemo(() => {
    if (chargerData?.ownerType === OWNER_TYPE.USER) {
      return chargerData.homeChargingPartnerId;
    }

    return chargerData?.ownerId;
  }, [chargerData]);

  const { role } = useAuth();
  const { canEditChargers: hasEditPermissionsOnCharger, canViewChargerWifiPasswords } =
    useCompanyPermissions(chargerCompanyId);
  const canEditChargers =
    hasEditPermissionsOnCharger ||
    (chargerData?.ownerType === OWNER_TYPE.USER && role === USER_ROLE.SUPPORT);

  const { data: WifiPassword } = useFetchWifiPasswordQuery(
    {
      id: id!,
      scope: userScope!,
    },
    {
      skip: !canViewChargerWifiPasswords,
    },
  );

  const [triggerGetCircuits, { data: circuitsData }] = useLazyGetCircuitsQuery();

  const [triggerValidAutoStart, autoStartQueryResult] = useIsValidChargerMutation();

  const filteredCircuits = circuitsData?.entities.filter(
    (circuit) => circuit.locationId === chargerData?.location?.id,
  );

  useEffect(() => {
    const errorObj = error as { originalStatus: number };
    if (errorObj && errorObj.originalStatus === 404) {
      navigate(RoutePath.NOT_FOUND, { replace: true });
    }
  }, [error, navigate]);

  const charger = useMemo(() => {
    return chargerData || ({} as Charger);
  }, [chargerData]);

  useEffect(() => {
    if (chargerCompanyId) {
      triggerGetCircuits({
        scope: SCOPE_TYPE.COMPANY,
        companyId: chargerCompanyId,
      });
    }
  }, [chargerCompanyId, triggerGetCircuits]);

  const { manufacturerModelData } = useFetchModelsQuery(undefined, {
    selectFromResult: (endpoint) => ({
      manufacturerModelData: endpoint.data ? endpoint.data : [],
    }),
  });

  const [updateChargerDetail, setUpdateChargerDetail] = useState({} as UpdateChargerInformation);

  const { license } = useFetchLicenseByLicenseKeyQuery(
    {
      scope: userScope!,
      companyId: charger.ownerId,
      licenseKey: charger.licenseKey,
    },
    {
      skip: !charger.licenseKey,
      selectFromResult: (endPoint) => ({
        license: endPoint.data ? endPoint.data : null,
      }),
    },
  );

  useEffect(() => {
    if (charger) {
      setUpdateChargerDetail({
        id: id || '',
        displayId: charger?.name || '',
        chargerModelId: charger.chargerModelId,
        locationId: charger?.location?.id || '',
        directions: charger?.directions,
        parkingSpot: charger?.parkingSpot,
        associatedBuildingUnit: charger?.associatedBuildingUnit,
        internalNote: charger?.internalNote,
        autoStartEnabled: !!charger?.autoStartEnabled,
        autoStartConfig: charger?.autoStartConfig,
        hiddenOnMap: charger?.hiddenOnMap,
        priceModelId: charger?.priceModelId,
        maintenanceFlag: charger?.maintenanceFlag,
        ocppId: charger?.ocppId,
        deploymentInfo: {
          deployedOverride: charger.deploymentInfo?.deployed,
          deployedDateOverride: charger.deploymentInfo?.deployedDate,
          installationDate: charger.deploymentInfo?.installationDate,
          installationPerson: charger.deploymentInfo?.installationPerson,
          installationEmail: charger.deploymentInfo?.installerContactEmail,
          connectivityType: charger?.deploymentInfo?.connectivityType
            ? charger?.deploymentInfo?.connectivityType
            : Connectivity.SIM_CARD,
          whiteLabeledSimCard: charger.deploymentInfo?.whiteLabeledSimCard,
          simCardType: charger.deploymentInfo?.simCardType,
          simCardProvider: charger.deploymentInfo?.simCardProvider,
          simCardId: charger.deploymentInfo?.simCardId,
          wifiName: charger.deploymentInfo?.wifiName,
          wifiPassword: WifiPassword?.wifiPassword,
        },
        trailerAccessibility:
          charger?.ports?.reduce((acc: any, port: any, index: number) => {
            acc[`${id}.${index + 1}`] = port.trailerAccessibility || 'NOT_INDICATED';
            return acc;
          }, {}) || {},
      });
    }
    if (!isChargerLoading) {
      setLoadSkeletonAfterEdit(false);
    }
  }, [charger, id, editEnabled, WifiPassword, isChargerLoading]);

  const handleInputChange = (key: string, value: any) => {
    setUpdateChargerDetail({ ...updateChargerDetail, [key]: value });
  };

  const displayToast = useCallback(() => {
    Snackbar.show({
      message: (
        <>
          <Label
            text={t('successfully_configured_auto_start_warning', {
              driverEmail: updateChargerDetail?.autoStartConfig?.email,
            })}
            type={LabelType.LABEL_S_MEDIUM}
            color={ColorType.BLACK}
          />
          <Label
            text={t('valid_payment_warning')}
            type={LabelType.LABEL_S_MEDIUM}
            color={ColorType.BLACK}
            className='pt-[24px]'
          />
        </>
      ),
      position: AlertPosition.BOTTOM_LEFT,
      alertType: AlertType.DEFAULT,
      duration: 5000,
      messageLabelType: LabelType.LABEL_S_MEDIUM,
      messageLabelColor: ColorType.BLACK,
      icon: Info,
      height: 'h-auto',
      iconColor: getHexColorByType(ColorType.WARNING),
      component: 'auto-start',
    });
  }, [updateChargerDetail?.autoStartConfig?.email]);

  useEffect(() => {
    if (autoStartQueryResult.isError && !autoStartQueryResult.isLoading) {
      displayToast();
    }
  }, [autoStartQueryResult, displayToast]);

  const renderNav = (parent: string, name?: string) => {
    return (
      <div className='flex flex-row gap-1'>
        <Label
          text={parent}
          type={LabelType.BODY2}
          color={ColorType.DENIM}
          onClick={() => switchCompany(companyId!, RoutePath.CHARGERS)}
        />
        <Label text={`/ ${name}`} type={LabelType.BODY2} color={ColorType.GREY6} />
      </div>
    );
  };

  const handleSaveBtnClick = () => {
    updateCharger({
      body: updateChargerDetail,
      params: {
        scope: userScope!,
        ...(chargerCompanyId && { companyId: chargerCompanyId }),
      },
    });
    captureAnalyticsEvent(AnalyticsEvent.CHARGER_DETAILS_UPDATE_EVENT, {
      chargerId: id,
    });
  };

  const handleCancelBtnClick = () => {
    captureAnalyticsEvent(AnalyticsEvent.CHARGER_DETAILS_UPDATE_CANCELLED_EVENT, {
      chargerId: id,
    });
    setEditEnabled(false);
    setManufacturer(charger.vendor);
  };

  const onSaveValidation = () => {
    return !(
      !updateChargerDetail.displayId ||
      !updateChargerDetail.chargerModelId ||
      !updateChargerDetail.ocppId ||
      !handleValidateId(updateChargerDetail.ocppId) ||
      !updateChargerDetail.deploymentInfo?.connectivityType ||
      !updateChargerDetail.priceModelId ||
      // Need to keep save button disabled when AutoStart Switch is enabled & Driver is selected but no email id is added
      (updateChargerDetail.autoStartConfig?.type === SIMULATED &&
        updateChargerDetail.autoStartConfig?.email !== undefined &&
        !validateEmail(updateChargerDetail.autoStartConfig?.email)) ||
      !isValidDisplayIdOCPP.ocppId ||
      !isValidDisplayIdOCPP.displayId
    );
  };
  const renderBtnDiv = () => {
    if (editEnabled) {
      return (
        <div className='flex flex-row-reverse gap-2'>
          <Button
            label={t('save')}
            type={ButtonType.PRIMARY}
            disabled={!onSaveValidation()}
            size={ButtonSize.SMALL}
            isSumbit
            dataTestId='chargers-save-details'
          />
          <Button
            label={t('cancel')}
            type={ButtonType.TERTIARY}
            size={ButtonSize.SMALL}
            onClick={handleCancelBtnClick}
          />
        </div>
      );
    }
  };

  const verifyAutoStart = () => {
    // verify when users enable autostart wth session === Driver and email is not empty or email changed

    const shouldVerify =
      updateChargerDetail.autoStartConfig?.type === SIMULATED &&
      updateChargerDetail.autoStartConfig?.email &&
      updateChargerDetail.autoStartConfig?.email !== charger.autoStartConfig?.email;

    if (shouldVerify) {
      triggerValidAutoStart({
        params: {
          failOnInvalidPaymentMethod: true,
          validateOnly: true,
          scope: userScope!,
          ...(chargerCompanyId && { companyId: chargerCompanyId }),
        },
        body: updateChargerDetail,
        id,
      });
    }
  };

  const renderInfoHeader = () => {
    if (editEnabled) {
      return (
        <div className='flex justify-start'>
          <Label text={t('charger_edit_details')} type={LabelType.H4} color={ColorType.BLACK} />
        </div>
      );
    }
    return (
      <>
        <Label
          text={t('charger_details_header')}
          type={LabelType.H4}
          color={ColorType.BLACK}
          isLoading={isLoading}
          skeletonHeight='h-6'
          className='truncate max-w-[364px]'
        />

        {canEditChargers && (
          <div className='absolute right-0 hover:bg-grey2 py-1 px-1 xl:px-2 rounded-full xl:rounded-lg'>
            <Icon
              src={Edit2}
              onClick={() => setEditEnabled(!editEnabled)}
              dataTestId='chargers-details-edit-button'
            />
          </div>
        )}
      </>
    );
  };

  const renderChargerSummary = () => {
    return (
      <ChargerSummary
        charger={charger}
        license={license}
        isLoading={(isChargerLoading && isLoading) || (isChargerLoading && loadSkeletonAfterEdit)}
        companyId={chargerCompanyId}
      />
    );
  };

  const renderChargerDetail = () => (
    <div className='w-[453px]'>
      <Card>
        <Form
          onSubmit={handleSaveBtnClick}
          queryResult={updateChargerQueryResult}
          onQuerySuccess={() => {
            verifyAutoStart();
            setEditEnabled(false);
            setLoadSkeletonAfterEdit(true);
          }}
          onQueryFailed={() => {
            setEditEnabled(false);
          }}
        >
          <div className='flex flex-row justify-between relative'>
            {renderInfoHeader()}
            {renderBtnDiv()}
          </div>
          <div className='divide-y divide-grey2'>
            <ChargerInfo
              charger={charger}
              license={license}
              editEnabled={editEnabled}
              manufacturer={manufacturer}
              setManufacturer={setManufacturer}
              isLoading={isLoading}
              handleInputChange={handleInputChange}
              updateChargerDetail={updateChargerDetail}
              setUpdateChargerDetail={setUpdateChargerDetail}
              manufacturerModelData={manufacturerModelData}
              setisValidDisplayIdOCPP={setisValidDisplayIdOCPP}
            />
            <AdditionalInfo
              charger={charger}
              editEnabled={editEnabled}
              isLoading={isLoading}
              handleInputChange={handleInputChange}
              updateChargerDetail={updateChargerDetail}
            />
            {allowPayterView && (
              <ChargerTerminal
                charger={charger}
                editEnabled={editEnabled && canEditTerminals}
                handleInputChange={handleInputChange}
              />
            )}
            <ChargerSetting
              charger={charger}
              editEnabled={editEnabled}
              isLoading={isLoading}
              handleInputChange={handleInputChange}
              updateChargerDetail={updateChargerDetail}
              updateChargerQueryResult={updateChargerQueryResult}
              filteredCircuits={filteredCircuits}
              canViewChargerWifiPasswords={canViewChargerWifiPasswords}
            />
            <ChargerAutoStart
              charger={charger}
              editEnabled={editEnabled}
              isLoading={isLoading}
              updateChargerDetail={updateChargerDetail}
              setUpdateChargerDetail={setUpdateChargerDetail}
            />
            <ChargerDeployment
              charger={charger}
              editEnabled={editEnabled}
              isLoading={isLoading}
              handleInputChange={handleInputChange}
              updateChargerDetail={updateChargerDetail}
            />
          </div>
        </Form>
      </Card>
    </div>
  );

  const renderSessions = useMemo(() => {
    if (!charger) {
      return null;
    }
    return (
      <ChargerHistory
        charger={charger}
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        chargerTimeZone={chargerData?.location?.timeZone!}
        companyId={chargerCompanyId!}
      />
    );
  }, [charger, chargerCompanyId, chargerData?.location?.timeZone]);

  return (
    <div className='flex flex-col gap-6'>
      {renderNav(t('chargers'), charger?.name || '')}
      <div className='flex flex-row gap-6'>
        <div className='flex flex-col gap-2'>
          {renderChargerSummary()}
          {renderChargerDetail()}
        </div>
        {renderSessions}
      </div>
    </div>
  );
};
